import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import { SHADOW } from "components/design/effects";
import { typography } from "components/design/typography";
import { ButtonType } from "components/atoms/Button";
import {
  FloatingButtonSize,
  FloatingButtonUsage,
} from "components/atoms/FloatingButton";

interface FloatingButtonWrapperProps {
  buttonType: ButtonType;
  size: FloatingButtonSize;
  color: FloatingButtonUsage;
  left?: boolean;
  right?: boolean;
}

const FloatingButtonWrapper = styled.button<FloatingButtonWrapperProps>`
  min-width: ${({ size }) => {
    const minWidthMap = {
      XLarge: "72px",
      Large: "48px",
      Medium: "40px",
    };

    return minWidthMap[size];
  }};
  height: ${({ size }) => {
    const heightMap = {
      XLarge: "72px",
      Large: "48px",
      Medium: "40px",
    };

    return heightMap[size];
  }};
  background-color: ${({ color }) => {
    const backgroundColorMap = {
      Primary: COLOR_SYSTEM.get("Blue")?.[400],
      Secondary: COLOR_SYSTEM.get("Gray")?.[700],
      Tertiary: COLOR_SYSTEM.get("Gray")?.[0],
    };

    return backgroundColorMap[color];
  }};
  ${({ size }) => {
    const fontMap = {
      XLarge: typography.get("InputXLarge"),
      Large: typography.get("InputLarge"),
      Medium: typography.get("InputMedium"),
    };

    return fontMap[size];
  }};
  color: ${({ color }) =>
    color === "Tertiary"
      ? COLOR_SYSTEM.get("Gray")?.[400]
      : COLOR_SYSTEM.get("Gray")?.[0]};
  border: ${({ color }) =>
    color === "Tertiary"
      ? `1px solid ${COLOR_SYSTEM.get("Gray")?.[50]}`
      : "none"};
  border-radius: ${({ size, buttonType }) => {
    const borderRadiusMap = {
      Text: {
        XLarge: "48px",
        Large: "48px",
        Medium: "48px",
      },
      Icon: "50%",
      IconWithText: {
        XLarge: "48px",
        Large: "48px",
        Medium: "48px",
      },
    };

    return buttonType === "Icon"
      ? borderRadiusMap["Icon"]
      : borderRadiusMap[buttonType][size];
  }};

  display: inline-flex;
  justify-content: center;
  align-items: center;

  ${({ size }) => {
    const shadowMap = {
      XLarge: SHADOW.get("4"),
      Large: SHADOW.get("3"),
      Medium: SHADOW.get("2"),
    };

    return shadowMap[size];
  }};
  cursor: pointer;
`;

export const IconFloatingButtonWrapper = styled(FloatingButtonWrapper)`
  padding: ${({ size }) => {
    switch (size) {
      case "XLarge":
        return "22px";
      case "Large":
        return "10px";
      case "Medium":
      default:
        return "8px";
    }
  }};
`;

export const TextFloatingButtonWrapper = styled(FloatingButtonWrapper)`
  padding: ${({ size }) => {
    const paddingMap = {
      XLarge: "11.5px 20px",
      Large: "11.5px 20px",
      Medium: "8.5px 16px",
    };

    return paddingMap[size];
  }};
`;

export const IconWithTextFloatingButtonWrapper = styled(FloatingButtonWrapper)`
  padding: ${({ size, left, right }) => {
    const iconPos = left ? "left" : "right";
    const paddingMap = {
      left: {
        XLarge: "11.5px 20px 11.5px 12px",
        Large: "11.5px 20px 11.5px 12px",
        Medium: "8.5px 16px 8.5px 10px",
      },
      right: {
        XLarge: "11.5px 12px 11.5px 20px",
        Large: "11.5px 16px 11.5px 20px",
        Medium: "8.5px 10px 8.5px 16px",
      },
    };

    return paddingMap[iconPos][size];
  }};

  gap: ${({ size }) => {
    const gapMap = {
      XLarge: "12px",
      Large: "12px",
      Medium: "10px",
    };

    return gapMap[size];
  }};
`;
